/**
 * InstancesPanel
 *
 * Displays instances of a component across storyboards/pages.
 *
 * ## Views
 *
 * - **Grouped view** (default): Shows one card per storyboard that contains
 *   instances of the selected component. Always renders as group cards
 *   with storyboard name and count badge (if > 1).
 *
 * - **Drilled view**: When a storyboard group is clicked, shows all instances
 *   for that storyboard with "Page Name > Storyboard Name" breadcrumb.
 *
 * ## Architecture
 *
 * - useInstancesData: Syncs NavTree → store, returns grouped/drilled data
 * - useCaptureInstances: Captures screenshots in order (no visibility prioritization)
 */

import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTypedSelector } from "@/store/store";
import { InstanceCard } from "./InstanceCard";
import { useSaveInstanceAsStory } from "./useSaveInstanceAsStory";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import {
  getInstanceUniqueKey,
  useInstancesStore,
  type Instance,
  type SelectedInstanceInfo,
} from "./instancesStore";
import { TempoElement } from "@/utils/tempoElement";
import type { NavTreeNode } from "@/utils/types";
import { useCanvasId } from "@/utils/utils";
import { Blocks, BookPlus, ChevronRight } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import {
  useInstancesData,
  useCurrentPageName,
  type StoryboardGroup,
} from "./useInstancesData";
import { useCaptureInstances } from "./useCaptureInstances";
import { useInstanceEventHandlers } from "./useInstanceEventHandlers";

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Walks up the NavTreeNode parent chain and returns the first result
 * where predicate returns non-undefined. Used for finding containing instances.
 */
function findInAncestry<T>(
  startNode: NavTreeNode | undefined,
  predicate: (elementKey: string) => T | undefined,
): T | undefined {
  let node = startNode;
  while (node) {
    const codebaseId = node.tempoElement?.codebaseId;
    if (codebaseId) {
      const elementKey = node.tempoElement.getKey();
      const result = predicate(elementKey);
      if (result !== undefined) return result;
    }
    node = node.parent;
  }
  return undefined;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTEXT MENU
// ═══════════════════════════════════════════════════════════════════════════════

interface InstanceContextMenuProps {
  children: React.ReactNode;
  instance: Instance;
  onSaveAsStory: (instance: Instance) => void;
}

const InstanceContextMenu = React.memo(
  ({ children, instance, onSaveAsStory }: InstanceContextMenuProps) => {
    const handleSaveAsStory = React.useCallback(() => {
      onSaveAsStory(instance);
    }, [instance, onSaveAsStory]);

    // Only show context menu if we have drag data
    const canSaveAsStory = !!instance.dragData.fileContents;

    if (!canSaveAsStory) {
      return <>{children}</>;
    }

    return (
      <ContextMenu>
        <ContextMenuTrigger>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <ContextMenuItem onClick={handleSaveAsStory}>
            <BookPlus className="mr-2 h-4 w-4" />
            Save as Story
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);
InstanceContextMenu.displayName = "InstanceContextMenu";

// ═══════════════════════════════════════════════════════════════════════════════
// SCREENSHOT PREVIEW
// ═══════════════════════════════════════════════════════════════════════════════

interface ScreenshotPreviewProps {
  url: string | undefined;
  alt: string;
  hasError: boolean;
  onError: () => void;
}

/**
 * Reusable screenshot preview component with gradient background and fallback icon.
 */
const ScreenshotPreview = React.memo(
  ({ url, alt, hasError, onError }: ScreenshotPreviewProps) => (
    <div
      className="w-full aspect-square min-h-[40px] rounded-lg flex items-center justify-center"
      style={{
        background: "linear-gradient(90deg, #1F1F22 0%, #2F3035 100%)",
      }}
    >
      <div className="h-full w-full p-2 flex items-center justify-center overflow-hidden rounded-sm bg-secondary-n-50">
        {url && !hasError ? (
          <img
            src={url}
            alt={alt}
            className="object-scale-down rounded-sm max-w-full max-h-full"
            draggable={false}
            onError={onError}
          />
        ) : (
          <Blocks className="w-8 h-8 text-gray-400" />
        )}
      </div>
    </div>
  ),
);
ScreenshotPreview.displayName = "ScreenshotPreview";

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface InstancesPanelProps {
  selectedAsset: JsxElement;
}

export const InstancesPanel = React.memo(
  ({ selectedAsset }: InstancesPanelProps) => {
    const canvasId = useCanvasId();

    // View state - null = grouped view, storyboardId = drilled view
    const [selectedStoryboardId, setSelectedStoryboardId] = useState<
      string | null
    >(null);

    // Track which image URLs have failed to load
    const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(
      () => new Set(),
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // HOOKS
    // ═══════════════════════════════════════════════════════════════════════════

    // 1. Get instance data (syncs + reads from store, organized by page/storyboard)
    const {
      currentPageStoryboardGroups,
      otherPagesGroups,
      drilledInstances,
      selectedStoryboardInfo,
      instancesToCapture,
      componentFileKey,
    } = useInstancesData(selectedAsset, selectedStoryboardId);

    // 2. Capture instances (in order, as they appear)
    const { captureInstance } = useCaptureInstances(
      selectedAsset,
      instancesToCapture,
    );

    const currentPageName = useCurrentPageName();

    // ═══════════════════════════════════════════════════════════════════════════
    // STORE SUBSCRIPTIONS
    // ═══════════════════════════════════════════════════════════════════════════

    // Subscribe to screenshot data URLs (temporary, before upload)
    const screenshotDataUrls = useInstancesStore(
      (state) => state.screenshotDataUrls,
    );

    // Subscribe to permanent screenshot URLs from store
    const instanceScreenshotUrls = useInstancesStore((state) => {
      if (!canvasId || !componentFileKey) return {};
      const componentData =
        state.instancesByCanvas[canvasId]?.[componentFileKey];
      if (!componentData) return {};

      const urls: Record<string, string | undefined> = {};
      for (const storyboardData of Object.values(componentData)) {
        for (const [instanceKey, instance] of Object.entries(storyboardData)) {
          urls[instanceKey] = instance.contentWindowData?.screenshotUrl;
        }
      }
      return urls;
    });

    // Helper to get display URL for an instance
    const getDisplayUrl = useCallback(
      (instanceKey: string): string | undefined => {
        // Priority: permanent URL > temporary dataUrl
        return (
          instanceScreenshotUrls[instanceKey] || screenshotDataUrls[instanceKey]
        );
      },
      [instanceScreenshotUrls, screenshotDataUrls],
    );

    // For switching pages when navigating to instances on other pages
    const selectedPageId = usePagesStore((state) =>
      canvasId ? state.selectedPageId[canvasId] : undefined,
    );

    // Save instance as story hook
    const { saveInstanceAsStory } = useSaveInstanceAsStory(selectedAsset);

    // Event handlers hook
    const {
      handleDragStart,
      handleDragEnd,
      handleMouseEnter,
      handleMouseLeave,
      handleClick,
      handleDoubleClick,
    } = useInstanceEventHandlers({
      canvasId,
      selectedPageId,
      selectedAssetCodebaseId: selectedAsset.codebaseId,
      captureInstance,
    });

    // ═══════════════════════════════════════════════════════════════════════════
    // CANVAS SELECTION SYNC
    // ═══════════════════════════════════════════════════════════════════════════

    const selectedElementKey = useTypedSelector(
      (state) => state.iframe.selectedElementKey,
    );
    const selectedCanvasInstance = useInstancesStore(
      (state) => state.selectedCanvasInstance,
    );
    const sbIdToCodebaseIdToNavNodeLookup = useCanvasStore(
      (state) => state.sbIdToCodebaseIdToNavNodeLookup,
    );

    // Refs for scrolling to selected instances/groups
    const instanceRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const drilledViewTopRef = useRef<HTMLDivElement>(null);

    // Track previous values to detect changes
    const prevSelectedElementKeyRef = useRef<string | null>(null);
    const prevSelectedPageIdRef = useRef<string | null | undefined>(undefined);

    // Sync canvas selection to instance store and trigger capture on selection change
    useEffect(() => {
      const store = useInstancesStore.getState();
      let info: SelectedInstanceInfo | null = null;

      if (selectedElementKey) {
        // First try direct O(1) lookup
        info = store.elementKeyToInstanceInfo[selectedElementKey] ?? null;

        // If no direct match, walk up ancestry to find containing instance
        if (!info) {
          const parsed = TempoElement.fromKey(selectedElementKey);
          if (!parsed.isEmpty()) {
            const { codebaseId, storyboardId, uniquePath } = parsed;
            const navNodes =
              sbIdToCodebaseIdToNavNodeLookup[storyboardId]?.[codebaseId];
            const startNode = navNodes?.find(
              (n) => n.tempoElement?.uniquePath === uniquePath,
            );
            info =
              findInAncestry(
                startNode,
                (elementKey) => store.elementKeyToInstanceInfo[elementKey],
              ) ?? null;
          }
        }
      }

      if (store.selectedCanvasInstance?.instanceKey === info?.instanceKey) {
        return;
      }
      store.setSelectedCanvasInstance(info);

      // Only trigger capture if selectedElementKey actually changed
      const selectionChanged =
        selectedElementKey !== prevSelectedElementKeyRef.current;
      prevSelectedElementKeyRef.current = selectedElementKey;

      if (selectionChanged && info && canvasId && componentFileKey) {
        // In drilled view, check if instance is in the selected storyboard
        const isInCurrentView = selectedStoryboardId
          ? info.storyboardId === selectedStoryboardId
          : true;

        if (isInCurrentView) {
          const instance =
            store.instancesByCanvas[canvasId]?.[componentFileKey]?.[
              info.storyboardId
            ]?.[info.instanceKey];
          if (instance) {
            captureInstance(instance);
          }
        }
      }
    }, [
      selectedElementKey,
      canvasId,
      componentFileKey,
      selectedStoryboardId,
      captureInstance,
      sbIdToCodebaseIdToNavNodeLookup,
    ]);

    // Auto-scroll to selected instance or storyboard group
    useEffect(() => {
      if (!selectedCanvasInstance) return;

      const { instanceKey, storyboardId } = selectedCanvasInstance;

      if (selectedStoryboardId) {
        // Drilled view - scroll to specific instance
        const el = instanceRefs.current.get(instanceKey);
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } else {
        // Grouped view - scroll to storyboard group
        const el =
          groupRefs.current.get(storyboardId) ||
          instanceRefs.current.get(instanceKey);
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, [selectedCanvasInstance, selectedStoryboardId]);

    // When drilling into a storyboard that contains the selected instance, scroll to it
    useEffect(() => {
      if (!selectedStoryboardId || !selectedCanvasInstance) return;
      if (selectedCanvasInstance.storyboardId !== selectedStoryboardId) return;

      requestAnimationFrame(() => {
        const el = instanceRefs.current.get(selectedCanvasInstance.instanceKey);
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    }, [selectedStoryboardId, selectedCanvasInstance]);

    // When page changes in drilled view, scroll to top
    useEffect(() => {
      const pageChanged = selectedPageId !== prevSelectedPageIdRef.current;
      prevSelectedPageIdRef.current = selectedPageId;

      if (pageChanged && selectedStoryboardId && drilledViewTopRef.current) {
        drilledViewTopRef.current.scrollIntoView({
          behavior: "instant",
          block: "start",
        });
      }
    }, [selectedPageId, selectedStoryboardId]);

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    // Render a single instance card with selection outline and context menu
    const renderInstanceCard = (instance: Instance, isOtherPages: boolean) => {
      const instanceKey = getInstanceUniqueKey(instance, canvasId!);
      const displayUrl = getDisplayUrl(instanceKey);
      const isSelected = selectedCanvasInstance?.instanceKey === instanceKey;

      return (
        <div
          key={instanceKey}
          ref={(el) => {
            if (el) instanceRefs.current.set(instanceKey, el);
            else instanceRefs.current.delete(instanceKey);
          }}
          className="rounded-lg"
          style={
            isSelected
              ? { outline: "1px solid var(--primary-n-500)" }
              : undefined
          }
        >
          <InstanceContextMenu
            instance={instance}
            onSaveAsStory={saveInstanceAsStory}
          >
            <InstanceCard
              instanceId={instance.instanceCodebaseId}
              storyboardId={instance.storyboardId}
              instanceFilepath={instance.instanceFilepath}
              screenshotUrl={displayUrl}
              hasImageError={!!displayUrl && failedImageUrls.has(displayUrl)}
              onImageError={() =>
                displayUrl &&
                setFailedImageUrls((prev) => new Set(prev).add(displayUrl))
              }
              width={instance.contentWindowData.width}
              height={instance.contentWindowData.height}
              onClick={() => handleClick(instance)}
              onDoubleClick={() => handleDoubleClick(instance)}
              onMouseEnter={
                isOtherPages ? undefined : () => handleMouseEnter(instance)
              }
              onMouseLeave={isOtherPages ? undefined : handleMouseLeave}
              onDragStart={(e) => handleDragStart(e, instance)}
              onDragEnd={handleDragEnd}
            />
          </InstanceContextMenu>
        </div>
      );
    };

    // Render a grid of storyboard groups (grouped view)
    const renderStoryboardsGrid = (storyboardGroups: StoryboardGroup[]) => (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2">
        {storyboardGroups.map((group) => {
          const { storyboardId, storyboardName, count, firstInstance } = group;
          const instanceKey = getInstanceUniqueKey(firstInstance, canvasId!);
          const displayUrl = getDisplayUrl(instanceKey);
          const isGroupSelected =
            selectedCanvasInstance?.storyboardId === storyboardId;

          return (
            <div
              key={storyboardId}
              ref={(el) => {
                if (el) groupRefs.current.set(storyboardId, el);
                else groupRefs.current.delete(storyboardId);
              }}
              className="p-2 rounded-lg cursor-pointer transition-all border bg-secondary-n-950 hover:bg-secondary-n-700 border-border-primary hover:border-primary-0/50"
              style={
                isGroupSelected
                  ? { outline: "1px solid var(--primary-n-500)" }
                  : undefined
              }
              onClick={() => setSelectedStoryboardId(storyboardId)}
            >
              <ScreenshotPreview
                url={displayUrl}
                alt={`Storyboard ${storyboardName}`}
                hasError={!!displayUrl && failedImageUrls.has(displayUrl)}
                onError={() =>
                  displayUrl &&
                  setFailedImageUrls((prev) => new Set(prev).add(displayUrl))
                }
              />
              <div className="flex items-center justify-between mt-2 gap-1 min-h-[28px]">
                <span
                  className="text-2xs font-medium text-secondary-n-50 truncate"
                  title={storyboardName}
                >
                  {storyboardName}
                </span>
                {count > 1 && (
                  <span className="text-xs text-primary rounded-xs bg-secondary-300 px-2 py-1">
                    {count}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );

    // Render a grid of individual instances (drilled view)
    const renderInstancesGrid = (
      instances: Instance[],
      isOtherPages = false,
    ) => (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2">
        {instances.map((instance) =>
          renderInstanceCard(instance, isOtherPages),
        )}
      </div>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════

    // Drilled view - shows all instances in the selected storyboard
    if (selectedStoryboardId && selectedStoryboardInfo) {
      return (
        <>
          {/* Breadcrumb: Page Name > Storyboard Name */}
          <div
            ref={drilledViewTopRef}
            className="border-t border-border-primary pt-3 mt-3 mb-2"
          >
            <div className="flex items-center gap-1">
              <span
                className="text-xs font-medium text-secondary-n-400 cursor-pointer hover:text-secondary-n-200"
                onClick={() => setSelectedStoryboardId(null)}
              >
                {selectedStoryboardInfo.pageName}
              </span>
              <ChevronRight className="w-3 h-3 text-secondary-n-600" />
              <span className="text-xs font-medium text-secondary-n-400 truncate">
                {selectedStoryboardInfo.storyboardName}
              </span>
            </div>
          </div>

          {/* All instances in this storyboard */}
          {renderInstancesGrid(drilledInstances, false)}
        </>
      );
    }

    // No storyboard groups to show
    const hasAnyGroups =
      currentPageStoryboardGroups.length > 0 || otherPagesGroups.length > 0;
    if (!hasAnyGroups) {
      return null;
    }

    // Grouped view - storyboards organized by page
    return (
      <>
        {/* Current Page Storyboard Groups */}
        {currentPageStoryboardGroups.length > 0 && (
          <div className="border-t border-border-primary pt-3 mt-3">
            <div className="mb-2">
              <span className="text-xs font-medium text-secondary-n-400">
                Instances on {currentPageName}
              </span>
            </div>
            {renderStoryboardsGrid(currentPageStoryboardGroups)}
          </div>
        )}

        {/* Other Pages - each page with its storyboard groups */}
        {otherPagesGroups.map((pageGroup, index) => (
          <div
            key={pageGroup.pageId}
            className={
              index === 0 && currentPageStoryboardGroups.length === 0
                ? "border-t border-border-primary pt-3 mt-3"
                : "pt-3 mt-3"
            }
          >
            <div className="mb-2">
              <span className="text-xs font-medium text-secondary-n-400">
                {pageGroup.pageName}
              </span>
            </div>
            {renderStoryboardsGrid(pageGroup.storyboardGroups)}
          </div>
        ))}
      </>
    );
  },
);

InstancesPanel.displayName = "InstancesPanel";
